import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, Input, Modal,
  Option, Select, Sheet, Stack, Table, Textarea, Typography,
} from '@mui/joy';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useAdmin } from '../../AdminContext';
import { APIAdminReferrals, APIAdminReferralUpdate } from '../../api_admin_referrals';
import { APIAdminGetSetting, APIAdminUpdateSetting } from '../../api_admin_settings';

const STATUS = {
  pending: { label: 'Ожидает', color: 'neutral' },
  confirmed: { label: 'Подтверждён', color: 'success' },
  rejected: { label: 'Отклонён', color: 'danger' },
};
const KIND_FILTER = [
  { value: '', label: 'все' },
  { value: 'user_tg', label: 'Пользователь (TG)' },
  { value: 'user_max', label: 'Пользователь (Max)' },
  { value: 'partner', label: 'Партнёр' },
  { value: 'advertiser', label: 'Бизнес' },
];

function StatusChip({ s }) {
  const m = STATUS[s] || { label: s, color: 'neutral' };
  return <Chip size="sm" variant="soft" color={m.color}>{m.label}</Chip>;
}

function ConditionsCard({ token }) {
  const [text, setText] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try { const s = await APIAdminGetSetting(token, 'referral_conditions'); if (alive) setText(s?.value || ''); }
      catch { /* ignore */ } finally { if (alive) setLoaded(true); }
    })();
    return () => { alive = false; };
  }, [token]);

  const save = async () => {
    setSaving(true); setOk(false);
    try {
      await APIAdminUpdateSetting(token, 'referral_conditions', text, 'Условия реферальной программы');
      setOk(true); setTimeout(() => setOk(false), 2000);
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  return (
    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2 }}>
      <Typography level="title-sm" sx={{ mb: 0.5 }}>Условия программы</Typography>
      <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
        Свободный текст условий (пока справочно, виден только в админке). Менять — superadmin/finance.
      </Typography>
      <Textarea minRows={2} value={text} disabled={!loaded} onChange={(e) => setText(e.target.value)} sx={{ mb: 1 }} />
      <Stack direction="row" spacing={1} alignItems="center">
        <Button size="sm" loading={saving} onClick={save}>Сохранить условия</Button>
        {ok && <Typography level="body-sm" color="success">Сохранено</Typography>}
      </Stack>
    </Sheet>
  );
}

function EditModal({ token, item, open, onClose, onSaved }) {
  const [statusV, setStatusV] = useState('pending');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && item) { setStatusV(item.status); setComment(item.admin_comment || ''); setError(null); }
  }, [open, item]);

  const save = async () => {
    setBusy(true); setError(null);
    try { await APIAdminReferralUpdate(token, item.id, { status: statusV, admin_comment: comment }); onSaved?.(); }
    catch { setError('Не удалось сохранить (нужна роль moderator/superadmin)'); } finally { setBusy(false); }
  };

  if (!item) return null;
  return (
    <Modal open={open} onClose={onClose}>
      <Sheet variant="outlined" sx={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        width: { xs: '94%', sm: 480 }, borderRadius: 'lg', p: 3,
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography level="h4">Связь #{item.id}</Typography>
          <IconButton size="sm" variant="plain" onClick={onClose}><CloseRoundedIcon /></IconButton>
        </Stack>
        <Stack spacing={1.2}>
          {error && <Alert color="danger">{error}</Alert>}
          <Typography level="body-sm">
            <b>{item.referrer_name}</b> ({item.referrer_kind_label}) → <b>{item.referred_name}</b> ({item.referred_kind_label})
          </Typography>
          <Typography level="body-xs" color="neutral">
            Код: {item.code} · источник: {item.source} · {new Date(item.date_create).toLocaleString('ru-RU')}
          </Typography>
          <Divider />
          <Box>
            <Typography level="body-xs" color="neutral">Статус</Typography>
            <Select size="sm" value={statusV} onChange={(_, v) => v && setStatusV(v)} sx={{ maxWidth: 220 }}>
              {Object.entries(STATUS).map(([k, m]) => <Option key={k} value={k}>{m.label}</Option>)}
            </Select>
          </Box>
          <Box>
            <Typography level="body-xs" color="neutral">Комментарий</Typography>
            <Textarea minRows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
          </Box>
          <Stack direction="row" spacing={1}>
            <Button loading={busy} onClick={save}>Сохранить</Button>
          </Stack>
        </Stack>
      </Sheet>
    </Modal>
  );
}

export default function AdminReferralsPage() {
  const { adminToken: token } = useAdmin();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusF, setStatusF] = useState('');
  const [referrerF, setReferrerF] = useState('');
  const [referredF, setReferredF] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [edit, setEdit] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setSearch(searchInput); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const r = await APIAdminReferrals(token, {
        search: search || undefined, status: statusF || undefined,
        referrer_kind: referrerF || undefined, referred_kind: referredF || undefined,
        page, page_size: 20,
      });
      setItems(r?.items || []); setCounts(r?.counts || {});
      setPages(r?.pages || 1); setTotal(r?.total || 0);
    } catch { setError('Не удалось загрузить'); } finally { setLoading(false); }
  }, [token, search, statusF, referrerF, referredF, page]);
  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Typography level="h4" sx={{ mb: 0.5 }}>Рефералы</Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        <Chip variant="soft">Всего: {total}</Chip>
        <Chip variant="soft" color="neutral">Ожидают: {counts.pending || 0}</Chip>
        <Chip variant="soft" color="success">Подтверждены: {counts.confirmed || 0}</Chip>
        <Chip variant="soft" color="danger">Отклонены: {counts.rejected || 0}</Chip>
      </Stack>

      <ConditionsCard token={token} />

      <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Input size="sm" placeholder="Код" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} sx={{ flex: 1 }} />
          <Select size="sm" value={statusF} onChange={(_, v) => { setPage(1); setStatusF(v); }} sx={{ minWidth: 150 }}>
            <Option value="">Все статусы</Option>
            {Object.entries(STATUS).map(([k, m]) => <Option key={k} value={k}>{m.label}</Option>)}
          </Select>
          <Select size="sm" value={referrerF} onChange={(_, v) => { setPage(1); setReferrerF(v); }} sx={{ minWidth: 190 }}>
            {KIND_FILTER.map((f) => <Option key={f.value} value={f.value}>Пригласил: {f.label}</Option>)}
          </Select>
          <Select size="sm" value={referredF} onChange={(_, v) => { setPage(1); setReferredF(v); }} sx={{ minWidth: 190 }}>
            {KIND_FILTER.map((f) => <Option key={f.value} value={f.value}>Пришёл: {f.label}</Option>)}
          </Select>
        </Stack>
      </Sheet>

      {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
      ) : (
        <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
          <Table hoverRow sx={{ '--TableCell-paddingY': '10px' }}>
            <thead>
              <tr>
                <th style={{ width: 60 }}>ID</th><th>Пригласил</th><th>Пришёл</th>
                <th style={{ width: 120 }}>Код</th><th style={{ width: 130 }}>Статус</th>
                <th style={{ width: 110 }}>Дата</th><th style={{ width: 100 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td>{r.referrer_name}<br />
                    <Typography level="body-xs" color="neutral">{r.referrer_kind_label}</Typography></td>
                  <td>{r.referred_name}<br />
                    <Typography level="body-xs" color="neutral">{r.referred_kind_label}</Typography></td>
                  <td>{r.code}</td>
                  <td><StatusChip s={r.status} /></td>
                  <td>{new Date(r.date_create).toLocaleDateString('ru-RU')}</td>
                  <td><Button size="sm" variant="soft" onClick={() => setEdit(r)}>Открыть</Button></td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={7}>
                  <Typography level="body-sm" color="neutral" sx={{ py: 2, textAlign: 'center' }}>Пусто</Typography>
                </td></tr>
              )}
            </tbody>
          </Table>
        </Sheet>
      )}

      {pages > 1 && (
        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
          <Button size="sm" variant="outlined" disabled={page <= 1} onClick={() => setPage((x) => x - 1)}>Назад</Button>
          <Chip variant="soft">{page} / {pages}</Chip>
          <Button size="sm" variant="outlined" disabled={page >= pages} onClick={() => setPage((x) => x + 1)}>Вперёд</Button>
        </Stack>
      )}

      <EditModal token={token} item={edit} open={!!edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </Box>
  );
}